import { type RefObject, useCallback, useEffect, useRef, useState } from "react"

import { addMinutes, differenceInMinutes } from "date-fns"

import { layoutPixelScale } from "@/lib/pointerCoords"
import { autoAdjustStops } from "@/lib/stopSchedule"
import { snapToInterval } from "@/lib/timelineScale"
import type { Route, Stop } from "@/types/collections"

export const SNAP_INTERVAL = 5

export type DragType = "move" | "resize-left" | "resize-right"

interface DragState {
  routeId: string
  stopId: string
  type: DragType
  startX: number
  originalStop: Stop
  originalStops: Stop[]
}

interface StopDragOptions {
  routes: Route[]
  hourWidth: number
  isToday: boolean
  timelineContentRef: RefObject<HTMLDivElement>
  snapshotRoute: (route: Route) => void
  applyDraggedStops: (routeId: string, stops: Stop[]) => void
}

export function useStopDrag({
  routes,
  hourWidth,
  isToday,
  timelineContentRef,
  snapshotRoute,
  applyDraggedStops,
}: StopDragOptions) {
  const [dragState, setDragState] = useState<DragState | null>(null)
  const isDraggingRef = useRef(false)

  const handleDragStart = useCallback(
    (e: React.MouseEvent, stop: Stop, route: Route, type: DragType) => {
      if (!isToday || route.isLocked || stop.status === "completed") return

      e.preventDefault()
      e.stopPropagation()
      isDraggingRef.current = true

      snapshotRoute(route)

      setDragState({
        routeId: route.id,
        stopId: stop.id,
        type,
        startX: e.clientX,
        originalStop: { ...stop },
        originalStops: [...route.stops],
      })
    },
    [isToday, snapshotRoute]
  )

  const handleDragMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState || !isDraggingRef.current) return

      // `clientX` is painted and `hourWidth` is layout, so convert. See lib/pointerCoords.
      const deltaX = (e.clientX - dragState.startX) * layoutPixelScale(timelineContentRef.current)
      const deltaMinutes = (deltaX / hourWidth) * 60

      const route = routes.find((r) => r.id === dragState.routeId)
      if (!route) return

      const originalStop = dragState.originalStop
      let newArrival: Date
      let newDeparture: Date

      if (dragState.type === "move") {
        newArrival = snapToInterval(
          addMinutes(originalStop.plannedArrival, deltaMinutes),
          SNAP_INTERVAL
        )
        newDeparture = snapToInterval(
          addMinutes(originalStop.plannedDeparture, deltaMinutes),
          SNAP_INTERVAL
        )
      } else if (dragState.type === "resize-left") {
        newArrival = snapToInterval(
          addMinutes(originalStop.plannedArrival, deltaMinutes),
          SNAP_INTERVAL
        )
        newDeparture = originalStop.plannedDeparture
        if (differenceInMinutes(newDeparture, newArrival) < 5) {
          newArrival = addMinutes(newDeparture, -5)
        }
      } else {
        newArrival = originalStop.plannedArrival
        newDeparture = snapToInterval(
          addMinutes(originalStop.plannedDeparture, deltaMinutes),
          SNAP_INTERVAL
        )
        if (differenceInMinutes(newDeparture, newArrival) < 5) {
          newDeparture = addMinutes(newArrival, 5)
        }
      }

      const adjustedStops = autoAdjustStops(
        dragState.originalStops,
        dragState.stopId,
        newArrival,
        newDeparture,
        { skipCompleted: true, moveEstimate: true }
      )

      applyDraggedStops(dragState.routeId, adjustedStops)
    },
    [dragState, routes, hourWidth, applyDraggedStops, timelineContentRef]
  )

  // Nothing recorded here: the edge function logs the movement on save, so an abandoned drag leaves
  // no history.
  const handleDragEnd = useCallback(() => {
    isDraggingRef.current = false
    setDragState(null)
  }, [])

  useEffect(() => {
    if (dragState) {
      window.addEventListener("mousemove", handleDragMove)
      window.addEventListener("mouseup", handleDragEnd)
      document.body.style.cursor = dragState.type === "move" ? "grabbing" : "ew-resize"
      document.body.style.userSelect = "none"

      return () => {
        window.removeEventListener("mousemove", handleDragMove)
        window.removeEventListener("mouseup", handleDragEnd)
        document.body.style.cursor = ""
        document.body.style.userSelect = ""
      }
    }
  }, [dragState, handleDragMove, handleDragEnd])

  return {
    draggingStopId: dragState?.stopId ?? null,
    isDragging: dragState !== null,
    handleDragStart,
  }
}
